import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCar, faCarSide, faFileLines, faToolbox } from '@fortawesome/free-solid-svg-icons';
import VehicleBrandsPanel from './vehicleManagement/VehicleBrandsPanel';
import VehicleModelsPanel from './vehicleManagement/VehicleModelsPanel';
import VehicleInquiryChecklistsPanel from './vehicleManagement/VehicleInquiryChecklistsPanel';
import VehicleInquiryChecklistEditor from './vehicleManagement/VehicleInquiryChecklistEditor';
import VehicleInquiryChecklistViewer from './vehicleManagement/VehicleInquiryChecklistViewer';
import VehicleRelatedProductsPage from './vehicleManagement/VehicleRelatedProductsPage';
import BaysPanel from './vehicleManagement/BaysPanel';

const NAV_LINKS = [
  {
    key: 'brands',
    label: 'Brands',
    icon: faCar,
    description: 'Create and maintain the list of vehicle brands available in the workshop.',
  },
  {
    key: 'models',
    label: 'Models',
    icon: faCarSide,
    description: 'Manage vehicle models and map them to their respective brands.',
  },
  {
    key: 'checklists',
    label: 'Checklists',
    icon: faFileLines,
    description:
      'Create reusable checklist questions and link them to specific vehicle brands or models.',
  },
  {
    key: 'bays',
    label: 'Bays',
    icon: faToolbox,
    description: 'Define service bays/workstations and map them to locations.',
  },
];

const VehicleManagement: FC = () => {
  const navigate = useNavigate();
  const { section, id, subsection } = useParams<{ section?: string; id?: string; subsection?: string }>();

  useEffect(() => {
    if (!section) {
      navigate(`/vehicle-management/${NAV_LINKS[0].key}`, { replace: true });
    }
  }, [section, navigate]);

  const activeKey = useMemo(() => {
    const normalized = section?.toLowerCase();
    return NAV_LINKS.some((link) => link.key === normalized)
      ? (normalized as string)
      : NAV_LINKS[0].key;
  }, [section]);

  const activeLink = useMemo(
    () => NAV_LINKS.find((link) => link.key === activeKey) ?? NAV_LINKS[0],
    [activeKey],
  );

  const handleSelect = (key: string) => {
    if (key === activeKey) {
      return;
    }

    navigate(`/vehicle-management/${key}`);
  };

  const showSectionSummary = !id && !subsection;

  return (
    <div className="rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)] min-h-[calc(100vh-220px)]">
      <div className="flex h-full flex-col gap-6 md:flex-row">
        <aside className="hidden basis-40 shrink-0 flex-col gap-1.5 rounded-2xl border border-slate-200/70 bg-[#f3f5ff] p-3 shadow-sm lg:flex">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Vehicle management</h2>
          {NAV_LINKS.map((item) => {
            const isActive = item.key === activeKey;
            return (
              <button
                key={item.key}
                type="button"
                className={`flex items-center gap-3 rounded-xl px-3 py-2 text-left transition ${isActive
                  ? 'bg-primary/10 text-primary shadow-sm'
                  : 'text-slate-600 hover:bg-white/70 hover:text-primary'
                  }`}
                onClick={() => handleSelect(item.key)}
              >
                <span className="text-lg">
                  <FontAwesomeIcon icon={item.icon} className="w-5 h-5" />
                </span>
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </aside>

        <section className="flex-1 min-w-0 rounded-2xl border border-slate-200/70 bg-white p-1 shadow-sm">
          {showSectionSummary ? (
            <header className="border-b border-slate-200 p-5 shadow-sm">
              <h1 className="text-2xl font-semibold text-slate-800">{activeLink.label}</h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-500">{activeLink.description}</p>
            </header>
          ) : null}

          <div className="p-5">
            {activeLink.key === 'brands' ? (
              subsection === 'related-products' && id ? (
                <VehicleRelatedProductsPage />
              ) : (
                <VehicleBrandsPanel />
              )
            ) : activeLink.key === 'models' ? (
              subsection === 'related-products' && id ? (
                <VehicleRelatedProductsPage />
              ) : (
                <VehicleModelsPanel />
              )
            ) : activeLink.key === 'bays' ? (
              <BaysPanel />
            ) : id && subsection === undefined ? (
              <VehicleInquiryChecklistEditor />
            ) : id && subsection === 'view' ? (
              <VehicleInquiryChecklistViewer />
            ) : (
              <VehicleInquiryChecklistsPanel />
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default VehicleManagement;
